import { useNavigate } from "react-router-dom";

interface CourseCardProps {
  id: string;
  title: string;
  description: string;
  duration: string;
  modules: number;
  image: string;
  brand?: string;
  isPrototype?: boolean;
  category?: string;
}

function PovosDoBrasilCard({ id, title, description, duration, modules, image, category }: CourseCardProps) {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(`/curso/${id}`)}
      className="group relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:scale-105 border-2 border-[#C85C2A]/20 hover:border-[#C85C2A]/60"
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A0A00]/70 via-transparent to-transparent"></div>

        {/* "Em breve" badge */}
        <div className="absolute top-3 left-3 bg-[#E8A42A] text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
          <i className="ri-time-line text-xs"></i>
          Em breve
        </div>

        {/* Category pill */}
        <div className="absolute bottom-3 left-3 bg-[#C85C2A]/90 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-xs font-semibold">
          {category}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Povos do Brasil brand mark */}
        <div className="flex items-center gap-1.5 mb-3">
          <div className="w-3 h-3 rounded-full bg-[#C85C2A]"></div>
          <div className="w-3 h-3 rounded-full bg-[#E8A42A]"></div>
          <div className="w-3 h-3 rounded-full bg-[#2D6A4F]"></div>
          <span className="text-xs font-bold text-[#C85C2A] ml-1 tracking-wide uppercase">Povos do Brasil</span>
        </div>

        <h3 className="text-base font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#C85C2A] transition-colors leading-snug">
          {title}
        </h3>

        <p className="text-xs text-gray-500 mb-4 line-clamp-2">{description}</p>

        <div className="flex items-center justify-between pt-3 border-t border-[#C85C2A]/10">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <i className="ri-time-line text-[#C85C2A]"></i>
            <span>{duration}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <i className="ri-book-line text-[#C85C2A]"></i>
            <span>{modules} módulos</span>
          </div>
        </div>

        <button className="w-full mt-4 bg-[#C85C2A] hover:bg-[#A34520] text-white py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap cursor-pointer">
          Saiba mais
          <i className="ri-arrow-right-line ml-1.5"></i>
        </button>
      </div>

      {/* Prototype overlay hint */}
      <div className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center bg-white/90 rounded-full">
        <i className="ri-draft-line text-[#C85C2A] text-sm"></i>
      </div>
    </div>
  );
}

export default function CourseCard({
  id,
  title,
  description,
  duration,
  modules,
  image,
  brand,
  isPrototype,
  category,
}: CourseCardProps) {
  const navigate = useNavigate();

  if (brand === 'povos-do-brasil') {
    return (
      <PovosDoBrasilCard
        id={id}
        title={title}
        description={description}
        duration={duration}
        modules={modules}
        image={image}
        brand={brand}
        isPrototype={isPrototype}
        category={category}
      />
    );
  }

  return (
    <div className="group bg-white rounded-2xl overflow-hidden transition-all duration-500 border-2 border-[#0099CC]/20 hover:border-[#0099CC]/40 hover:scale-105 cursor-pointer">
      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-[#0099CC]/10 to-[#FFCC00]/10">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-3 right-3 bg-[#FFCC00] text-gray-900 px-3 py-1.5 rounded-full text-xs font-bold">
          Novo
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-[#0099CC] transition-colors">
          {title}
        </h3>

        <p className="text-sm text-gray-600 mb-4 line-clamp-3">{description}</p>

        <div className="flex items-center justify-between pt-4 border-t-2 border-[#0099CC]/10">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <i className="ri-time-line text-[#0099CC]"></i>
            <span>{duration}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <i className="ri-book-line text-[#0099CC]"></i>
            <span>{modules} módulos</span>
          </div>
        </div>

        <button
          onClick={() => navigate(`/curso/${id}`)}
          className="w-full mt-4 bg-[#0099CC] hover:bg-[#007AA3] text-white py-3 rounded-lg font-semibold transition-all hover:shadow-lg whitespace-nowrap cursor-pointer"
        >
          Ver Curso
        </button>
      </div>
    </div>
  );
}
